import gql from 'graphql-tag'
import React from 'react'
import { Trans } from '@lingui/macro'
import { useQuery } from '@apollo/client'

import Spinner from '../../../common/spinner/Spinner'
import { classForMarbleColorRating } from '../Marble'

import './RatingDetailsTooltip.less'

const RATING_DETAILS_QUERY = gql`
  query ratingDetails($topicId: PositiveInt!) {
    topic(id: $topicId) {
      id
      ... on TopicModelAsMember {
        ratingDetails
        averageRating
      }
      ... on TopicModelAsEditor {
        ratingDetails
        averageRating
      }
    }
  }
`

const RATING_TOPIC_VERSION_DETAILS_QUERY = gql`
  query ratingDetails($topicId: PositiveInt!, $topicVersionId: PositiveInt!) {
    topic(id: $topicId) {
      id
      ... on TopicModelAsEditor {
        topicVersion(id: $topicVersionId) {
          __typename
          id
          averageRating
          ratingDetails
        }
        __typename
      }
      __typename
    }
  }
`

const RatingDetailsTooltip = (props: { topicId: number }) => {
  const { data, loading, error } = useQuery(RATING_DETAILS_QUERY, {
    variables: {
      topicId: props.topicId
    }
  })

  let tooltipContent

  if (loading) {
    tooltipContent = <Spinner />
  }
  if (error) {
    tooltipContent = (
      <p className="RatingDetailsTooltip__content__error">
        <Trans>The was an error while loading rating details</Trans>
      </p>
    )
  }
  if (data) {
    const { averageRating, ratingDetails } = data.topic
    tooltipContent = (
      <>
        <RatingDetailsBars ratings={ratingDetails} />
        <div className="RatingDetailsTooltip__content__avg-rating">
          <em
            className={`bar ${classForMarbleColorRating(
              Math.round(averageRating)
            )}-fg`}
          >
            {averageRating}
          </em>
          <div className="center">
            <span>
              <Trans>Avg. Rating</Trans>
            </span>
            <small>
              <Trans>No date filter applied</Trans>
            </small>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="RatingDetailsTooltip">
      <div className="RatingDetailsTooltip__content">{tooltipContent}</div>
      <div className="RatingDetailsTooltip__arrow" />
    </div>
  )
}

export const TopicVersionRatingDetailsTooltip = (props: {
  topicId: number
  topicVersionId: number
}) => {
  const { data, loading, error } = useQuery(
    RATING_TOPIC_VERSION_DETAILS_QUERY,
    {
      variables: {
        topicId: props.topicId,
        topicVersionId: props.topicVersionId
      }
    }
  )

  let tooltipContent

  if (loading) {
    tooltipContent = <Spinner />
  }
  if (error) {
    tooltipContent = (
      <p className="RatingDetailsTooltip__content__error">
        <Trans>The was an error while loading rating details</Trans>
      </p>
    )
  }
  if (data) {
    const { averageRating, ratingDetails } = data.topic.topicVersion
    tooltipContent = (
      <>
        <RatingDetailsBars ratings={ratingDetails} />
        <div className="RatingDetailsTooltip__content__avg-rating">
          <em
            className={`bar ${classForMarbleColorRating(
              Math.round(averageRating)
            )}-fg`}
          >
            {averageRating}
          </em>
          <div className="center">
            <span>
              <Trans>Avg. Rating</Trans>
            </span>
            <small>
              <Trans>No date filter applied</Trans>
            </small>
          </div>
        </div>
      </>
    )
  }

  return (
    <div className="RatingDetailsTooltip">
      <div className="RatingDetailsTooltip__content">{tooltipContent}</div>
      <div className="RatingDetailsTooltip__arrow" />
    </div>
  )
}

interface IRatingDetailsBarsProps {
  ratings: number[]
}

const RatingDetailsBars = ({ ratings }: IRatingDetailsBarsProps) => {
  if (!Array.isArray(ratings)) {
    return null
  }
  const maxVotes = Math.max(...ratings)

  return (
    <ul className="RatingDetailsTooltip__content__bars">
      {[...ratings].reverse().map((votes, index) => {
        const rating = 5 - index
        const width = Math.round((votes / maxVotes) * 100)

        return (
          <RatingDetailsBar
            key={index}
            votes={votes}
            width={width}
            rating={rating}
          />
        )
      })}
    </ul>
  )
}

interface IRatingDetailsBarProps {
  rating: number
  width: number
  votes: number
}

const RatingDetailsBar = ({ rating, width, votes }: IRatingDetailsBarProps) => (
  <li>
    <em>{rating}</em>
    <div className={`bar__wrapper`} style={{ width: `100%` }}>
      <div
        className={`bar ${classForMarbleColorRating(rating)}-bg`}
        style={{ width: `${width}%` }}
      />
    </div>
    <span className="count">{votes > 0 ? votes : ''}</span>
  </li>
)

export default RatingDetailsTooltip
